import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://api.resources.aurx.co/api',
  timeout: 15000,
});

// Email sends are far slower than a normal API call: they can mint a Teams
// meeting over MS Graph, pull a resume from S3, then hand one message per
// recipient to SES sequentially. The default 15s aborts mid-send — the mail
// still goes out, but the UI reports a failure and invites a duplicate send.
const EMAIL_SEND_TIMEOUT = 120000;
const emailSend = { timeout: EMAIL_SEND_TIMEOUT };

// Resume uploads wait on S3, which the backend retries for up to ~40 s through
// a network blip. The browser must outlast that, or it reports a failure for a
// save that actually went through.
const UPLOAD_TIMEOUT = 120000;

// ── Frontend GET response cache (30-second TTL) ───────────────────────────────
const _responseCache = new Map();   // key → { data, expiresAt }
const _inFlight      = new Map();   // key → Promise
// Bumped on every bust. A GET that started before a bust may have read
// pre-mutation data, so it must not write its response into the cache.
let _cacheGeneration = 0;

const _cacheKey = (url, params) => `${url}|${JSON.stringify(params || {})}`;

/**
 * Bust all cached entries whose key starts with urlPrefix.
 * Call after any mutation to invalidate stale GET data.
 */
export const bustCache = (urlPrefix) => {
  _cacheGeneration += 1;
  for (const key of _responseCache.keys()) {
    if (key.startsWith(urlPrefix)) _responseCache.delete(key);
  }
  for (const key of _inFlight.keys()) {
    if (key.startsWith(urlPrefix)) _inFlight.delete(key);
  }
};

/**
 * Bust the GET cache for EVERY endpoint that embeds cross-entity data.
 * Candidate / client / requirement names (and recruiter names) are joined into
 * submissions, reports and dashboards — so editing any one of those entities
 * must refresh all of them, or the 30s cache serves a stale name (e.g. a
 * renamed candidate still showing the old name on the Reports screen).
 * Mirrors the backend cluster invalidation.
 */
export const bustEntityCaches = () => {
  ['/candidates', '/clients', '/requirements', '/submissions', '/reports'].forEach(bustCache);
};

// Endpoints that must NEVER be served from the frontend 30s cache. Two reasons:
//
//   (1) Correctness — a cached response is not just stale, it's WRONG:
//       /check-duplicate  a cached dup-check could let a duplicate through
//       /resume-url       presigned S3 URLs expire; a cached one can be dead
//       /impersonate      impersonation/grant state is security-sensitive
//       /settings/cache   the SuperAdmin toggle must show its true on/off state
//
//   (2) Freshness — on-demand reads where the user expects to see the result of
//       the action they JUST took (and the caching win is negligible because
//       these fire on explicit clicks, not on every page render):
//       /reports          generated via the "Generate" button
//       /activity-logs    history / journey / audit feed
//       /notifications    new + read state must be live
//       /full-profile     the candidate details modal must reflect latest edits
//       /sent-candidates  the "already emailed" indicator
//       /finder/search    search results must be live
//       /matched-candidates  requirement matching must be live
//       /emails/logs, /emails/log-senders  the Logs tab "Refresh" must show
//                         an email the user just sent
//
// Everything else (paginated lists, dropdowns, dashboard stats, entity gets,
// templates) stays cached and is busted on mutation via bustEntityCaches().
// Patterns are matched as substrings and were checked against every GET route
// for collisions (e.g. '/impersonate' does NOT match '/settings/impersonation',
// '/sent-candidates' does NOT match '/emails/log-senders').
const _NO_FRONTEND_CACHE = [
  '/recordings',      // answers carry 2h signed links and change as Teams publishes
  '/reports',
  '/activity-logs',
  '/notifications',
  '/check-duplicate',
  '/resume-url',
  '/full-profile',
  '/sent-candidates',
  '/interviewer-busy', // other recruiters book interviewers at any moment
  '/finder/search',
  '/matched-candidates',
  '/emails/logs',
  '/emails/log-senders',
  '/impersonate',
  '/settings/cache',
  '/settings/file',
  '/settings/teams',
  '/settings/db-backups',
  '/schedulers',        // next-run/last-run must reflect "Run now" immediately
];
const _isNoCache = (url) => _NO_FRONTEND_CACHE.some(p => url.includes(p));

// Override API.get to add caching + in-flight deduplication
const _originalGet = API.get.bind(API);
API.get = (url, config = {}) => {
  // Always-fresh endpoints skip the persistent 30s cache but STILL use in-flight
  // dedup below — merging two truly-simultaneous identical requests never yields
  // stale data (they share one fresh response) and it avoids duplicate hits from
  // endpoints polled/checked repeatedly on mount (e.g. /notifications).
  const noCache = _isNoCache(url);

  const key = _cacheKey(url, config.params);

  // Return cached response if still fresh (cacheable endpoints only)
  if (!noCache) {
    const cached = _responseCache.get(key);
    if (cached && Date.now() < cached.expiresAt) {
      return Promise.resolve(cached.data);
    }
  }

  // Return in-flight promise if request already underway
  if (_inFlight.has(key)) return _inFlight.get(key);

  const generation = _cacheGeneration;
  // Only clear our own in-flight entry: after a bust, a newer request for the
  // same key may already be registered under it.
  const release = () => { if (_inFlight.get(key) === promise) _inFlight.delete(key); };
  const promise = _originalGet(url, config).then((response) => {
    // Skip caching if any bust happened while this request was in flight —
    // its response may predate the mutation and would be served for 30s.
    if (!noCache && generation === _cacheGeneration) {
      _responseCache.set(key, { data: response, expiresAt: Date.now() + 30 * 1000 });
    }
    release();
    return response;
  }).catch((err) => {
    release();
    throw err;
  });

  _inFlight.set(key, promise);
  return promise;
};

// Request interceptor - attach token
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // The token this request was rejected for is no longer the stored one:
      // a parallel request (or another tab) already restored the actor after
      // an ended impersonation, or someone signed in again. Wiping storage now
      // would log out the session that replaced it — just fail this request.
      const headers = error.config?.headers;
      const sentAuth = typeof headers?.get === 'function' ? headers.get('Authorization') : headers?.Authorization;
      const sentToken = typeof sentAuth === 'string' ? sentAuth.replace(/^Bearer\s+/i, '') : null;
      const currentToken = localStorage.getItem('token');
      if (sentToken && currentToken && sentToken !== currentToken) {
        error.authSuperseded = true;
        return Promise.reject(error);
      }
      // If the 401 came from an ended (or expired) impersonation session AND we
      // have the actor's original token stashed, drop the user back into their
      // own session instead of forcing a full re-login. The error is tagged so
      // AuthContext does not treat it as a logout and undo the restore.
      if (error.response?.data?.impersonationEnded) {
        const originalToken = localStorage.getItem('originalToken');
        const originalUser  = localStorage.getItem('originalUser');
        if (originalToken) {
          localStorage.setItem('token', originalToken);
          if (originalUser) localStorage.setItem('user', originalUser);
          localStorage.removeItem('originalToken');
          localStorage.removeItem('originalUser');
          error.impersonationRestored = true;
          window.location.href = '/dashboard';
          return Promise.reject(error);
        }
      }
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Redirect WITH an error marker so Login.js shows the manual sign-in screen
      // instead of auto-redirecting to Zoho. A bare /login here re-fires SSO and,
      // combined with any token rejection, produces the SSO redirect loop. This
      // branch only runs when a token was actually sent and rejected, so it never
      // affects a clean first-time visit (which has no token and no API call).
      window.location.href = '/login?error=session_expired';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (data) => API.post('/auth/login', data),
  getMe: () => API.get('/auth/me'),
  register: (data) => API.post('/auth/register', data).then(r => { bustCache('/auth/users'); return r; }),
  changePassword: (data) => API.put('/auth/change-password', data),
  getUsers: (params) => API.get('/auth/users', { params }),
  // Active users a task can be assigned to — { users: [{ id, name, email, role }] }.
  // Open to SuperAdmin + RecruiterManager (GET /auth/users is SuperAdmin-only).
  // Busted with '/auth/users' by register/updateUser/deleteUser.
  getAssignableUsers: () => API.get('/auth/users/assignable'),
  // updateUser/deleteUser also bust impersonation caches because role/active
  // changes affect who shows up in the granted /users list.
  updateUser: (id, data) => API.put(`/auth/users/${id}`, data).then(r => {
    bustCache('/auth/users');
    bustCache('/auth/impersonate');
    bustEntityCaches();   // a recruiter's name is denormalized into submissions + reports
    return r;
  }),
  deleteUser: (id) => API.delete(`/auth/users/${id}`).then(r => {
    bustCache('/auth/users');
    bustCache('/auth/impersonate');
    bustEntityCaches();
    return r;
  }),
  // Impersonation
  // Each mutation busts the cached GETs for /auth/impersonate/* so reopening
  // a modal or rendering the sidebar after an action sees fresh data — without
  // this the 30-second response cache silently returns stale state.
  startImpersonation: (targetUserId, data) =>
    API.post(`/auth/impersonate/${targetUserId}`, data || {})
       .then(r => { bustCache('/auth/impersonate'); return r; }),
  stopImpersonation:  ()                   =>
    API.post('/auth/impersonate/stop')
       .then(r => { bustCache('/auth/impersonate'); return r; }),
  getActiveImpersonation: ()               => API.get('/auth/impersonate/active'),
  // Impersonation grants (SuperAdmin manages; everyone reads their own)
  listImpersonationGrants:   (actorId)     => API.get('/auth/impersonate/grants', { params: actorId ? { actorId } : {} }),
  listMyImpersonationGrants: ()            => API.get('/auth/impersonate/grants/mine'),
  createImpersonationGrant:  (data)        =>
    API.post('/auth/impersonate/grants', data)
       .then(r => { bustCache('/auth/impersonate/grants'); return r; }),
  deleteImpersonationGrant:  (id)          =>
    API.delete(`/auth/impersonate/grants/${id}`)
       .then(r => { bustCache('/auth/impersonate/grants'); return r; }),
  // SSO helpers — full URLs used for browser redirects (not XHR)
  ssoLoginUrl:    () => `${API.defaults.baseURL}/auth/sso/login`,
  ssoMetadataUrl: () => `${API.defaults.baseURL}/auth/sso/metadata`,
};

// Client APIs
export const clientAPI = {
  getAll: (params) => API.get('/clients', { params }),
  dropdown: () => API.get('/clients/dropdown'),       // all clients — id+name only
  getById: (id) => API.get(`/clients/${id}`),
  create: (data) => API.post('/clients', data).then(r => { bustEntityCaches(); return r; }),
  update: (id, data) => API.put(`/clients/${id}`, data).then(r => { bustEntityCaches(); return r; }),
  delete: (id) => API.delete(`/clients/${id}`).then(r => { bustEntityCaches(); return r; }),
  assignManager: (id, data) => API.post(`/clients/${id}/assign-manager`, data).then(r => { bustEntityCaches(); return r; }),
  assignRecruiters: (id, data) => API.post(`/clients/${id}/assign-recruiters`, data).then(r => { bustEntityCaches(); return r; }),
  sendReport: (id) => API.post(`/clients/${id}/send-report`),
};

// Candidate APIs
export const candidateAPI = {
  getAll: (params) => API.get('/candidates', { params }),
  dropdown: () => API.get('/candidates/dropdown'),    // all candidates — id+name only
  getById: (id) => API.get(`/candidates/${id}`),
  // New candidate + resume PDF in ONE request, so they're saved together or not at all.
  create: (data, resumeFile) => {
    const fd = new FormData();
    fd.append('data', JSON.stringify(data));
    if (resumeFile) fd.append('resume', resumeFile);
    return API.post('/candidates', fd, { timeout: UPLOAD_TIMEOUT }).then(r => { bustEntityCaches(); return r; });
  },
  update: (id, data) => API.put(`/candidates/${id}`, data).then(r => { bustEntityCaches(); return r; }),
  delete: (id) => API.delete(`/candidates/${id}`).then(r => { bustEntityCaches(); return r; }),
  updateStatus: (id, status) => API.put(`/candidates/${id}/status`, { status }).then(r => { bustEntityCaches(); return r; }),
  // Upload PDF resume — data must be a FormData object
  uploadResume: (id, formData) => API.post(`/candidates/${id}/upload-resume`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: UPLOAD_TIMEOUT,
  }).then(r => { bustEntityCaches(); return r; }),
  // Get pre-signed S3 URL for resume download
  getResumeUrl: (id) => API.get(`/candidates/${id}/resume-url`),
  checkDuplicate: (params) => API.get('/candidates/check-duplicate', { params }),
  parseResume:   (formData) => API.post('/candidates/parse-resume', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  finderSearch:   (params) => API.get('/candidates/finder/search', { params }),
  getFullProfile: (id)     => API.get(`/candidates/${id}/full-profile`),
};

// Requirement APIs
export const requirementAPI = {
  getAll: (params) => API.get('/requirements', { params }),
  dropdown: () => API.get('/requirements/dropdown'),  // all requirements — id+name only
  getById: (id) => API.get(`/requirements/${id}`),
  // Requirements are embedded in team dashboards and team requirement counts,
  // so '/teams' is busted too (else a Team Dashboard edit appears to revert).
  create: (data) => API.post('/requirements', data).then(r => { bustEntityCaches(); bustCache('/teams'); return r; }),
  update: (id, data) => API.put(`/requirements/${id}`, data).then(r => { bustEntityCaches(); bustCache('/teams'); return r; }),
  delete: (id) => API.delete(`/requirements/${id}`).then(r => { bustEntityCaches(); bustCache('/teams'); return r; }),
  getMatchedCandidates: (id) => API.get(`/requirements/${id}/matched-candidates`),
  getDashboardStats:   () => API.get('/requirements/dashboard/stats'),
  getMonthlyActivity: () => API.get('/requirements/dashboard/monthly'),
  // Fetch requirements filtered by client — for candidate form dropdown
  getByClient: (clientId) => API.get(`/requirements/by-client/${clientId}`),
};

// Activity Log APIs
export const activityAPI = {
  getAll:         (params) => API.get('/activity-logs', { params }),
  getByEntity:    (id)     => API.get(`/activity-logs/entity/${id}`),
  getFeed:        (params) => API.get('/activity-logs/feed', { params }),
  getDashboardFeed: (params) => API.get('/activity-logs/dashboard', { params }),
};

export const teamAPI = {
  getAll:        (p)        => API.get('/teams', { params: p }),
  getById:       (id)       => API.get(`/teams/${id}`),
  // Mutations bust the '/teams' GET cache so the list reflects changes
  // immediately instead of serving stale data for the 30s TTL.
  create:        (d)        => API.post('/teams', d).then(r => { bustCache('/teams'); return r; }),
  update:        (id, d)    => API.put(`/teams/${id}`, d).then(r => { bustCache('/teams'); bustEntityCaches(); return r; }),
  delete:        (id)       => API.delete(`/teams/${id}`).then(r => { bustCache('/teams'); bustEntityCaches(); return r; }),
  setMembers:    (id, d)    => API.post(`/teams/${id}/members`, d).then(r => { bustCache('/teams'); bustEntityCaches(); return r; }),
  removeMember:  (tid, uid) => API.delete(`/teams/${tid}/members/${uid}`).then(r => { bustCache('/teams'); bustEntityCaches(); return r; }),
  getStats:      ()         => API.get('/teams/stats'),
  getDashboard:  (id)       => API.get(`/teams/${id}/dashboard`),
};

export const taskAPI = {
  getAll:         (p)        => API.get('/tasks', { params: p }),
  getById:        (id)       => API.get(`/tasks/${id}`),
  // Mutations bust the '/tasks' GET cache so the board updates immediately.
  create:         (d)        => API.post('/tasks', d).then(r => { bustCache('/tasks'); return r; }),
  update:         (id, d)    => API.put(`/tasks/${id}`, d).then(r => { bustCache('/tasks'); return r; }),
  delete:         (id)       => API.delete(`/tasks/${id}`).then(r => { bustCache('/tasks'); return r; }),
  updateStatus:   (id, s)    => API.put(`/tasks/${id}/status`, { status: s }).then(r => { bustCache('/tasks'); return r; }),
  addComment:     (id, c)    => API.post(`/tasks/${id}/comments`, { comment: c }).then(r => { bustCache('/tasks'); return r; }),
  getDashboardStats: ()      => API.get('/tasks/dashboard/stats'),
};

export const notificationAPI = {
  getAll:      (p)  => API.get('/notifications', { params: p }),
  markRead:    (id) => API.put(`/notifications/${id}/read`),
  markAllRead: ()   => API.put('/notifications/read-all/mark'),
};

export const emailAPI = {
  // Templates
  getTemplates:   (p)       => API.get('/emails/templates', { params: p }),
  getTemplate:    (id)      => API.get(`/emails/templates/${id}`),
  createTemplate: (d)       => API.post('/emails/templates', d).then(r => { bustCache('/emails/templates'); return r; }),
  updateTemplate: (id, d)   => API.put(`/emails/templates/${id}`, d).then(r => { bustCache('/emails/templates'); return r; }),
  deleteTemplate:  (id)      => API.delete(`/emails/templates/${id}`).then(r => { bustCache('/emails/templates'); return r; }),
  toggleTemplate:  (id)      => API.patch(`/emails/templates/${id}/toggle`).then(r => { bustCache('/emails/templates'); return r; }),
  // Compose
  preview:        (d)       => API.post('/emails/preview', d),
  send:           (d)       => API.post('/emails/send', d, emailSend),
  sendJd:         (d)       => API.post('/emails/send-jd', d, emailSend),
  sendBulk:       (d)       => API.post('/emails/send-bulk', d, emailSend),
  sendToClient:   (d)       => API.post('/emails/send-to-client', d, emailSend),
  // Both stamp round data server-side (meetingLink / inviteSentAt /
  // interviewerEmails) — bust the submissions cache so reopening the modal
  // within 30s sees the fresh rounds.
  sendInterview:          (d)  => API.post('/emails/send-interview', d, emailSend).then(r => { bustCache('/submissions'); return r; }),
  cancelInterview:        (d)  => API.post('/emails/cancel-interview', d, emailSend).then(r => { bustCache('/submissions'); return r; }),
  getRequirementContacts: (id) => API.get(`/emails/requirement-contacts/${id}`),
  getLogs:        (p)       => API.get('/emails/logs', { params: p }),
  getLogSenders:  ()        => API.get('/emails/log-senders'),
  // Returns { lastSentAt: { candidateId: ISOString } } for a requirement —
  // powers the "already sent" indicator on the batch send-to-client tab.
  getSentCandidates: (p)    => API.get('/emails/sent-candidates', { params: p }),
};

export const reportAPI = {
  submissions:     (p) => API.get('/reports/submissions',      { params: p }),
  byRecruiter:     (p) => API.get('/reports/by-recruiter',     { params: p }),
  recruiterFunnel: (p) => API.get('/reports/recruiter-funnel', { params: p }),
  conversion:      (p) => API.get('/reports/conversion',       { params: p }),
};

// Restore jobs whose success has already cleared the frontend cache, so polling
// the finished job does not keep wiping it.
const _restoredJobsBusted = new Set();

export const settingsAPI = {
  getChecklist:      ()       => API.get('/settings/checklist'),
  // Checklist saves bust their GET cache so reopening the editor (or switching
  // requirements) doesn't show — and then re-save — the pre-save list.
  updateChecklist:   (items)  => API.put('/settings/checklist', { items })
                                    .then(r => { bustCache('/settings/checklist'); return r; }),
  getRequirementChecklist:    (reqId)        => API.get(`/settings/requirement-checklist/${reqId}`),
  updateRequirementChecklist: (reqId, items) => API.put(`/settings/requirement-checklist/${reqId}`, { items })
                                    .then(r => { bustCache('/settings/requirement-checklist'); return r; }),
  getEmailConfig:    ()       => API.get('/settings/email-config'),
  // Bust the 30s GET cache on save so reopening the modal (or the compose UI's
  // channel check) reflects the change immediately — prevents a stale re-save
  // silently reverting the just-saved senders/toggles.
  updateEmailConfig: (config) => API.put('/settings/email-config', config)
                                    .then(r => { bustCache('/settings/email-config'); return r; }),
  getImpersonationConfig:    ()       => API.get('/settings/impersonation'),
  updateImpersonationConfig: (config) => API.put('/settings/impersonation', config)
                                            .then(r => { bustCache('/settings/impersonation'); return r; }),
  getImpersonationPublicConfig: ()    => API.get('/settings/impersonation/public'),
  getCacheConfig:    ()              => API.get('/settings/cache'),
  // Accepts a boolean (on/off toggle → { enabled }) OR a full payload
  // ({ enabled?, ttls? }) from the TTL editor.
  updateCacheConfig: (payload)       => API.put('/settings/cache',
                                          typeof payload === 'boolean' ? { enabled: payload } : payload),
  getFileConfig:     ()              => API.get('/settings/file'),
  updateFileConfig:  (config)        => API.put('/settings/file', config),
  // Teams meeting-link credentials. The GET never returns the client secret —
  // only clientSecretSet — and the PUT only overwrites it when a new one is typed.
  getTeamsConfig:    ()              => API.get('/settings/teams'),
  updateTeamsConfig: (config)        => API.put('/settings/teams', config),

  // Database backups (SuperAdmin). Never cached (see _NO_FRONTEND_CACHE).
  listDbBackups:     ()              => API.get('/settings/db-backups'),
  updateDbBackupConfig: (config)     => API.put('/settings/db-backups/config', config),
  // Backup/restore run as background jobs: these POSTs only start one and
  // return 202 { job: { id, op, status:'running', startedAt } } — or 409 if a
  // backup/restore is already running on any pod. Track the outcome with
  // getDbBackupJob(). Starting a job takes a cluster lock and writes a ledger
  // row, so allow a little more than the default 15s on a slow DB.
  runDbBackup:       ()              => API.post('/settings/db-backups/run', {}, { timeout: 30000 }),
  // A restore replaces DB contents wholesale — every cached GET is now wrong.
  // bustCache('') clears the entire frontend cache (every key starts with '');
  // it is busted again by getDbBackupJob() once the restore has succeeded.
  restoreDbBackup:   (key, confirm)  => API.post('/settings/db-backups/restore', { key, confirm }, { timeout: 30000 })
    .then(r => { bustCache(''); return r; }),
  // Latest backup/restore job, from the DB so any pod can answer:
  // { job: null | { id, op, status:'running'|'succeeded'|'failed', startedAt, finishedAt, error, result } }.
  getDbBackupJob:    ()              => API.get('/settings/db-backups/job').then(r => {
    const job = r.data?.job;
    if (job && job.op === 'restore' && job.status === 'succeeded' && !_restoredJobsBusted.has(job.id)) {
      _restoredJobsBusted.add(job.id);
      bustCache('');
    }
    return r;
  }),
};

// Schedulers overview (SuperAdmin) — DB backup + per-client report schedulers
// with computed next-run times. Never cached (see _NO_FRONTEND_CACHE).
export const schedulerAPI = {
  getAll: () => API.get('/schedulers'),
};

// Server Logs is a live Server-Sent Events stream, read with fetch() rather
// than axios (which cannot stream) or EventSource (which cannot send the
// Authorization header). Not cached, never stored.
export const serverLogsAPI = {
  streamUrl: () => `${API.defaults.baseURL}/server-logs/stream`,
  token: () => localStorage.getItem('token'),
};

export const submissionAPI = {
  getAll:  (params)    => API.get('/submissions', { params }),
  getById: (id)        => API.get(`/submissions/${id}`),
  create:  (data)      => API.post('/submissions', data).then(r => { bustEntityCaches(); return r; }),
  update:  (id, data)  => API.put(`/submissions/${id}`, data).then(r => { bustEntityCaches(); return r; }),
  delete:  (id)        => API.delete(`/submissions/${id}`).then(r => { bustEntityCaches(); return r; }),
  getByCandidate: (candidateId) => API.get(`/submissions`, { params: { candidateId } }),
  // Interviewer addresses already used on rounds this user can see — powers
  // the type-ahead in the interview modal. Cheap and cacheable (30s).
  interviewerSuggestions: () => API.get('/submissions/interviewer-suggestions'),
  // Other interviews already booked for these interviewers that overlap a slot.
  interviewerBusy: (params) => API.get('/submissions/interviewer-busy', { params }),
  // The Teams recording(s) of one round. `refresh` asks the server to look in
  // OneDrive again (and the changing param also skips the 30s GET cache).
  // `roundId` (the round's stable id) makes the server find the round by id, so
  // renumbering after a delete can't point at another round; legacy rounds
  // without one are found by number.
  roundRecordings: (id, round, { refresh = false, roundId } = {}) => {
    const params = { ...(roundId ? { roundId } : {}), ...(refresh ? { refresh: 1, _: Date.now() } : {}) };
    return API.get(`/submissions/${id}/rounds/${round}/recordings`, { params: Object.keys(params).length ? params : undefined });
  },
};

// Absolute URL for an API path that the browser loads directly (a <video src>,
// a download link) rather than through axios. The base may itself be relative
// ('/api' behind the nginx proxy), so it is resolved against the page first.
export const apiUrl = (path) => new URL(path, new URL(API.defaults.baseURL, window.location.origin)).toString();
